import { Skeleton } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import AnimateHeight from "react-animate-height";
import { Link } from "react-router-dom";
import SessionsApi from "../../../api/sessionApi";
import { ReactComponent as ChevronIcon } from "../../../assets/Chat/chevron-up.svg";
import { ReactComponent as PlayCircle } from "../../../assets/Profile/specialist/play-circle.svg";
import { ReactComponent as Clock } from "../../../assets/SpecTools/clock.svg";
import CircleAvatar from "../../../components/CircleAvatar/CircleAvatar";
import ModalWindow from "../../../components/ModalWindow/ModalWindow";
import MultipleSelect from "../../../components/MultipleSelect/MultipleSelect";
import StandartButton from "../../../components/StandartButton/StandartButton";
import { ISO639Lang } from "../../../constans/languagesList";
import { ContextProvider } from "../../../contextProvider";
import { SessionPackage } from "../../../types/TypeSession";
import { formatDateAndTime } from "../../../utils/dateActions";
import { getTranslation } from "../../../utils/getTranslation";
import s from "./PackageCard.module.css";

type PackageCardProps = {
  sessionPackage: SessionPackage;
  approvePackage?: (packageId: string) => Promise<void>;
  denyPackage?: (packageId: string) => void;
  actionLoader?: boolean;
  isStaticProjectPackage?: boolean;
};

const PackageCard = ({
  sessionPackage,
  approvePackage,
  denyPackage,
  actionLoader,
  isStaticProjectPackage,
}: PackageCardProps) => {
  const { setUserIdForCreate, userData } = useContext(ContextProvider);
  const [packageFiles, setPackageFiles] = useState<{
    videoLinks:
      | {
          link: string;
          lang: string;
        }[]
      | undefined;
  }>({
    videoLinks: undefined,
  });
  const [videoModalIsOpen, setVideoModalIsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [selectPackageLang, setSelectPackageLang] = useState<string>(
    sessionPackage.title[0].language,
  );
  const [isPreviewReady, setIsPreviewReady] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const langData = sessionPackage.title.map((item) => ({
    lable: ISO639Lang[item.language],
    value: item.language,
  }));

  const requestedAtLabel = sessionPackage.approveRequestedAt
    ? (() => {
        const { formattedDate, formattedTime } = formatDateAndTime(
          new Date(sessionPackage.approveRequestedAt),
          userData?.selectedLanguage || "en",
        );
        return `${formattedDate} ${formattedTime}`;
      })()
    : undefined;

  const getAllSessionDuration = () => {
    if (!sessionPackage.sessions.length) return 0;
    const durationArr = sessionPackage.sessions.map(
      (item) => item.datesAndTime[0].duration,
    );
    let result = durationArr.reduce((accumulator, currentValue) => {
      return accumulator + currentValue;
    }, 0);
    result = Number((result / 60).toFixed(1));
    return result;
  };

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const img = imgRef.current;

    if (!video || !canvas || !img || !packageFiles.videoLinks) return;

    const handleLoadedMetadata = () => {
      video.currentTime = 0;
    };

    const handleTimeUpdate = () => {
      const context = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      if (!context) return;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataURL = canvas.toDataURL();
      img.src = dataURL;

      setIsPreviewReady(true); // ← добавлено
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [packageFiles, selectPackageLang]);

  useEffect(() => {
    const makeAsync = async () => {
      if (!sessionPackage) return;
      const response = await SessionsApi.getPackageFiles(sessionPackage._id);

      if (
        response.status &&
        response.packageVideos &&
        response.packageVideos[0]
      ) {
        setPackageFiles((prev) => ({
          ...prev,
          videoLinks: response.packageVideos!.map((item) => ({
            link: item.link,
            lang: item.language,
          })),
        }));
      }
      if (
        response.status &&
        response.packageImages &&
        response.packageImages[0]
      ) {
        setPackageFiles((prev) => ({
          ...prev,
          imageLink: response.packageImages![0].link,
        }));
      }
    };
    makeAsync();
  }, [sessionPackage]);

  return (
    <div className={s.container}>
      <div className={s.headBlock}>
        <MultipleSelect
          data={langData}
          multiplie={false}
          setValue={(value) => setSelectPackageLang(value as string)}
          value={selectPackageLang}
          width="200px"
        />
        {approvePackage && (
          <div className={s.actionBlock}>
            <Link
              to={`/chat?userId=${encodeURIComponent(
                sessionPackage.createdBy._id,
              )}`}
              onClick={() => setUserIdForCreate(sessionPackage.createdBy._id)}
              className={s.sendMessageBtn}
            >
              Send message
            </Link>
            <StandartButton
              buttonTitle={"Approve package"}
              action={() => approvePackage(sessionPackage._id)}
              disabled={actionLoader}
            />
            {denyPackage && (
              <button
                className={s.denyBtn}
                onClick={() => denyPackage(sessionPackage._id)}
                disabled={actionLoader}
              >
                Deny verification
              </button>
            )}
          </div>
        )}
      </div>
      <div className={s.mainContentBlock}>
        <div>
          <div className={s.flex20gap}>
            <h3 className={s.mainTitle}>
              {getTranslation(sessionPackage.title, selectPackageLang)}
            </h3>
            {sessionPackage.defaultForRecommend && (
              <div className={s.defaultBadge}>Default</div>
            )}
            <div className={s.pendingBadge}>Verification pending</div>
          </div>
          {requestedAtLabel && (
            <div className={s.reviewMeta}>Requested at: {requestedAtLabel}</div>
          )}
          <div className={s.generalInfoBlock}>
            <span>{sessionPackage.sessions.length + " sessions"}</span>
            <span>{getAllSessionDuration() + " hours total duration"}</span>
          </div>
          {sessionPackage.description && (
            <div
              className={s.description}
              dangerouslySetInnerHTML={{
                __html: sessionPackage.description
                  ? (sessionPackage.description.find(
                      (el) => el.language === selectPackageLang,
                    )?.text ?? sessionPackage.description[0].text)
                  : "",
              }}
            ></div>
          )}
          <AnimateHeight height={!collapsed ? "auto" : 0}>
            <div className={s.moreInfoBlock}>
              {sessionPackage.directions ? (
                <div className={s.tagsBlock}>
                  {sessionPackage.directions.map((item, index) => (
                    <span className={s.tagElement} key={index}>
                      <span className={s.indicator}></span>
                      {
                        item.labels.find(
                          (el) => el.language === selectPackageLang,
                        )?.text
                      }
                    </span>
                  ))}
                </div>
              ) : null}
              <div className={s.sessionListBlock}>
                {sessionPackage.sessions.map((item, index) => (
                  <div key={index} className={s.sessionElement}>
                    <h4 className={s.sessionTitle}>
                      {"Session" +
                        ` ${index + 1} - ` +
                        getTranslation(item.title, selectPackageLang)}
                    </h4>
                    {item.description && (
                      <span className={s.sessionDescription}>
                        {getTranslation(item.description, selectPackageLang)}
                      </span>
                    )}
                    <div className={s.sessionDurationBlock}>
                      <Clock />
                      {item.datesAndTime[0].duration + " m"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimateHeight>
          <div
            className={s.showmoreBtn}
            role="button"
            onClick={() => setCollapsed((prev) => !prev)}
          >
            {!collapsed ? "Show less" : "Show more"}
            <ChevronIcon
              className={`${s.chevronIcon} ${collapsed ? s.rotateChevron : ""}`}
            />
          </div>
        </div>
        {packageFiles.videoLinks?.length ? (
          <div className={s.videoPreviewBlock}>
            <canvas ref={canvasRef} style={{ display: "none" }} />

            <div className={s.previewWrapper}>
              <img
                ref={imgRef}
                alt="First frame of video"
                className={s.previewVideoImg}
                style={{ visibility: isPreviewReady ? "visible" : "hidden" }}
              />
              {!isPreviewReady && (
                <Skeleton
                  variant="rectangular"
                  width={130}
                  height={200}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    borderRadius: "20px",
                    zIndex: 999,
                    backgroundColor: "white",
                  }}
                />
              )}
            </div>

            <PlayCircle
              className={s.playBtn}
              onClick={() => setVideoModalIsOpen(true)}
            />
            <ModalWindow
              isOpen={videoModalIsOpen}
              setIsOpen={setVideoModalIsOpen}
              bgColor="transparent"
              width="fit-content"
            >
              <video
                src={
                  packageFiles.videoLinks.find(
                    (el) => el.lang === selectPackageLang,
                  )?.link
                }
                crossOrigin="anonymous"
                controls
                autoPlay
                className={s.video}
              />
            </ModalWindow>
          </div>
        ) : null}
      </div>
      {!isStaticProjectPackage && (
        <div className={s.bottomBlock}>
          <div className={s.flex20gap}>
            <span className={s.price}>
              $
              {(sessionPackage.packageCost.find(
                (el) => el.language === selectPackageLang,
              )?.amount ?? sessionPackage.packageCost[0].amount) / 100}
            </span>
          </div>

          <div className={s.specialistBlock}>
            <CircleAvatar userId={sessionPackage.createdBy._id} />
            <div className={s.specialistNameBlock}>
              <span>
                {getTranslation(
                  sessionPackage.createdBy.name,
                  selectPackageLang,
                )}{" "}
                {getTranslation(
                  sessionPackage.createdBy.surname,
                  selectPackageLang,
                )}
              </span>
              <span>
                {sessionPackage.specializations &&
                  sessionPackage.specializations[0] &&
                  getTranslation(
                    sessionPackage.specializations[0].labels,
                    selectPackageLang,
                  )}
              </span>
            </div>
          </div>
        </div>
      )}
      {packageFiles.videoLinks?.length ? (
        <video
          ref={videoRef}
          src={
            packageFiles.videoLinks.find((el) => el.lang === selectPackageLang)
              ?.link
          }
          style={{ display: "none" }}
          crossOrigin="anonymous"
        />
      ) : null}
    </div>
  );
};

export default PackageCard;
